import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { 
  Users, Send, Mail, UserPlus, Link2, X, Check
} from "lucide-react";
import { toast } from "sonner";
import { trackInsightRequestSent, trackInviteSent } from "../utils/analytics";
import SocialShareGrid from "./share/SocialShareGrid";

const API = '/api';

const InsightRequestDialog = ({ 
  open, 
  onOpenChange, 
  network, 
  toolType: initialToolType, 
  toolLabel: initialToolLabel, 
  onRequestSent,
  triggerButton,
  showToolPicker = false
}) => {
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [insightMessage, setInsightMessage] = useState("");
  const [sendingInsight, setSendingInsight] = useState(false);
  const [selectedTool, setSelectedTool] = useState(initialToolType || "swot");

  const toolType = showToolPicker ? selectedTool : initialToolType;
  const toolLabel = showToolPicker 
    ? (selectedTool === "swot" ? "Personal SWOT" : "Core Values")
    : initialToolLabel;

  // Invite new person state
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "" });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [shareData, setShareData] = useState(null);

  const acceptedNetwork = network.filter(c => c.status === 'accepted');

  const toggleConnection = (connId) => {
    setSelectedConnections(prev => 
      prev.includes(connId) 
        ? prev.filter(id => id !== connId)
        : [...prev, connId]
    );
  };

  const selectAll = () => {
    if (selectedConnections.length === acceptedNetwork.length) {
      setSelectedConnections([]);
    } else {
      setSelectedConnections(acceptedNetwork.map(c => c.connection_id));
    }
  };

  // Auto-generate the share link the moment the dialog opens. The token is
  // tool-tied (carries SWOT/Values context for the recipient), so we want it
  // ready before the user even thinks about which app to share to. Skips if
  // we already generated one for this session, or if the dialog is closed.
  useEffect(() => {
    if (!open) return;
    if (shareData?.inviteLink) return;
    if (generatingLink) return;
    let cancelled = false;
    (async () => {
      setGeneratingLink(true);
      try {
        const response = await fetch(`${API}/network/invite-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            tool_type: toolType,
            message: insightMessage || `I'd love your insights on my ${toolLabel}`,
          }),
        });
        if (response.ok && !cancelled) {
          const result = await response.json();
          const inviteLink = `${window.location.origin}/api/share/invite/${result.invite_token}`;
          setShareData({ inviteLink });
        }
      } catch {
        // Silent — user can still type an email or pick from network.
      } finally {
        if (!cancelled) setGeneratingLink(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, toolType]);

  const requestInsight = async () => {
    if (selectedConnections.length === 0) return;
    setSendingInsight(true);
    
    let successCount = 0;
    let failCount = 0;
    
    for (const connId of selectedConnections) {
      try {
        const response = await fetch(`${API}/insights/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            connection_id: connId,
            tool_type: toolType,
            message: insightMessage
          })
        });
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    
    setSendingInsight(false);
    
    if (successCount > 0) {
      const names = selectedConnections.map(id => {
        const conn = acceptedNetwork.find(c => c.connection_id === id);
        return conn?.display_name || conn?.name;
      }).filter(Boolean);
      
      if (successCount === 1) {
        toast.success(`Insight request sent to ${names[0]}!`);
      } else {
        toast.success(`Insight requests sent to ${successCount} people!`);
      }
      trackInsightRequestSent({ recipientCount: successCount, toolType });
      setSelectedConnections([]);
      setInsightMessage("");
      onOpenChange(false);
      onRequestSent?.();
    }
    if (failCount > 0 && successCount === 0) {
      toast.error('Failed to send requests');
    } else if (failCount > 0) {
      toast.error(`${failCount} request(s) failed`);
    }
  };

  const sendEmailInvite = async () => {
    if (!inviteForm.name || !inviteForm.email || !inviteForm.role) {
      toast.error('Please fill in all fields');
      return;
    }
    setSendingInvite(true);
    try {
      const response = await fetch(`${API}/network/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inviteForm)
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Invitation sent to ${inviteForm.name}!`);
        
        if (result.connection_id) {
          try {
            await fetch(`${API}/insights/request`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                connection_id: result.connection_id,
                tool_type: toolType,
                message: insightMessage || `I'd love your insights on my ${toolLabel}`
              })
            });
          } catch {}
        }
        
        setInviteForm({ name: "", email: "", role: "" });
        setInsightMessage("");
        onOpenChange(false);
        trackInviteSent("email");
        onRequestSent?.();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send invite');
      }
    } catch {
      toast.error('Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const generateShareLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await fetch(`${API}/network/invite-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tool_type: toolType,
          message: insightMessage || `I'd love your insights on my ${toolLabel}`
        })
      });
      if (response.ok) {
        const result = await response.json();
        const inviteLink = `${window.location.origin}/api/share/invite/${result.invite_token}`;
        setShareData({ inviteLink });
      } else {
        toast.error('Failed to generate link');
      }
    } catch {
      toast.error('Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const shareMessage = shareData
    ? `I'd love your insights on my ${toolLabel}. Join me on Evoluras: ${shareData.inviteLink}`
    : '';

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) {
      setShareData(null);
      setSelectedConnections([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {triggerButton && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'system-ui, sans-serif' }}>
            Request {toolLabel} Insight
          </DialogTitle>
        </DialogHeader>

        {/* Tabbed view — share grid lives inside the Invite-New tab so users
            can pick existing connections OR share/email a new person without
            switching to a separate "share link" screen. */}
        <Tabs defaultValue="network" className="mt-1">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="network" data-testid="tab-existing-network">
                <Users className="w-4 h-4 mr-1.5" />
                My Network
              </TabsTrigger>
              <TabsTrigger value="invite" data-testid="tab-invite-new">
                <UserPlus className="w-4 h-4 mr-1.5" />
                Invite New
              </TabsTrigger>
            </TabsList>

            {/* Existing Network Tab */}
            <TabsContent value="network" className="space-y-4 mt-3">
              {showToolPicker && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Which tool do you want insights on?
                  </label>
                  <Select value={selectedTool} onValueChange={setSelectedTool}>
                    <SelectTrigger data-testid="tool-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="swot">Personal SWOT</SelectItem>
                      <SelectItem value="core_values">Core Values</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Multi-select connection list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">
                    Select from your network
                  </label>
                  {acceptedNetwork.length > 1 && (
                    <button
                      onClick={selectAll}
                      className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                      data-testid="select-all-connections"
                    >
                      {selectedConnections.length === acceptedNetwork.length ? 'Deselect all' : 'Select all'}
                    </button>
                  )}
                </div>

                {acceptedNetwork.length > 0 ? (
                  <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto" data-testid="connection-list">
                    {acceptedNetwork.map((conn) => {
                      const isSelected = selectedConnections.includes(conn.connection_id);
                      const name = conn.display_name || conn.name;
                      const role = conn.display_role || conn.role;
                      return (
                        <label
                          key={conn.connection_id}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors border-b border-slate-100 last:border-b-0 ${
                            isSelected ? 'bg-violet-50' : 'hover:bg-slate-50'
                          }`}
                          data-testid={`connection-option-${conn.connection_id}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleConnection(conn.connection_id)}
                            className="data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
                          />
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={conn.display_picture} alt={name} />
                            <AvatarFallback className="bg-violet-100 text-violet-600 text-xs">
                              {name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{name}</p>
                            <p className="text-xs text-slate-500 capitalize">{role}</p>
                          </div>
                          {isSelected && <Check className="w-4 h-4 text-violet-600 flex-shrink-0" />}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-lg p-6 text-center">
                    <p className="text-sm text-slate-500">No connections yet — invite someone!</p>
                  </div>
                )}

                {selectedConnections.length > 0 && (
                  <p className="text-xs text-violet-600 mt-1.5" data-testid="selected-count">
                    {selectedConnections.length} {selectedConnections.length === 1 ? 'person' : 'people'} selected
                  </p>
                )}
              </div>

              <Button
                onClick={requestInsight}
                disabled={selectedConnections.length === 0 || sendingInsight}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                data-testid="send-insight-request-button"
              >
                {sendingInsight ? 'Sending...' : (
                  <>
                    Send Request{selectedConnections.length > 1 ? `s (${selectedConnections.length})` : ''}
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </TabsContent>

            {/* Invite New Tab */}
            <TabsContent value="invite" className="space-y-4 mt-3">
              {/* Share-link section — link auto-generates on dialog open so
                  users can hit any social tile right away. The shared
                  SocialShareGrid handles loading state internally (link
                  readout shows a placeholder while shareData is null). */}
              <div
                className="p-3 rounded-lg space-y-3"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" style={{ color: "var(--color-brand-primary)" }} />
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    Share a link
                  </span>
                </div>

                <SocialShareGrid
                  url={shareData?.inviteLink || ""}
                  message={shareMessage || `I'd love your insights on my ${toolLabel}.`}
                  shortText={`Insights on Evoluras`}
                  emailSubject="Quick favour — your insights on Evoluras"
                  nativeTitle="Evoluras"
                  testIdPrefix="insight-share"
                />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">or send an email</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Email invite */}
              <div className="space-y-3">
                <Input
                  placeholder="Name"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="insight-invite-name"
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="insight-invite-email"
                />
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger data-testid="insight-invite-role">
                    <SelectValue placeholder="Relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mentor">Mentor</SelectItem>
                    <SelectItem value="mentee">Mentee</SelectItem>
                    <SelectItem value="professor">Professor</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="coworker">Coworker</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="coachee">Coachee</SelectItem>
                    <SelectItem value="business_partner">Business Partner</SelectItem>
                    <SelectItem value="therapist">Therapist</SelectItem>
                    <SelectItem value="family">Family</SelectItem>
                    <SelectItem value="friend">Friend</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={sendEmailInvite}
                  disabled={sendingInvite}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                  data-testid="send-insight-email-invite"
                >
                  {sendingInvite ? 'Sending...' : 'Invite & Request Insight'}
                  <Mail className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>
          </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default InsightRequestDialog;
